import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { XMarkIcon } from '@heroicons/react/24/solid';
import { useLocation, useRouteLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';

import { useDisplayMode } from '~/hooks/useDisplayMode';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';

import Button from '../Button';
import { IosShareIcon } from './IosShareIcon';
import { isAndroidDevice } from './isAndroidDevice';
import { isIosDevice } from './isIosDevice';
import { showInstallPrompt } from './utils';

type InstallPromptProps = {
  debug?: boolean;
};

const InstallPrompt = ({ debug = false }: InstallPromptProps) => {
  return <ClientOnly>{() => <InstallPromptImpl debug={debug} />}</ClientOnly>;
};

function InstallPromptImpl({ debug = false }: InstallPromptProps) {
  const location = useLocation();
  // only show the install prompt from the home page BEE-9798
  const canShowPrompt = location.pathname === '/';
  const isAndroid = isAndroidDevice();
  const isIos = isIosDevice();
  const isMobile = isAndroid || isIos;
  const isLoggedIn = useIsLoggedIn();
  const displayMode = useDisplayMode();
  const isPwa = displayMode === 'standalone' || displayMode === 'fullscreen';
  const { publication } = (useRouteLoaderData('root') as any) ?? {};
  const [promptShown, setPromptShown] = usePromptShown();
  const [showPrompt, setShowPrompt] = useState(canShowPrompt && !promptShown && isPwa && isMobile && isLoggedIn);
  const { t } = useTranslation();

  // deferred prompt is set in entry.client.tsx from the beforeinstallprompt event
  const deferredPrompt = window.__DeferredPrompt__;

  // android install
  function onInstall() {
    if (!deferredPrompt) return;
    setPromptShown(true);

    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('Installing app');
      }
      setShowPrompt(false);
    });
  }

  function appInstalled() {
    toast.success(t('pwa.install_prompt.install_success'));
  }

  // setup installer support for android
  // biome-ignore lint/correctness/useExhaustiveDependencies: appInstalled changes on render
  useEffect(() => {
    window.addEventListener('appinstalled', appInstalled);
    return () => {
      window.removeEventListener('appinstalled', appInstalled);
    };
  }, []);

  // show prompt
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    function showInstallPrompt(e: any) {
      e.preventDefault();
      setShowPrompt(true);
      setPromptShown(true);
    }

    // setup event listener to show prompt
    // from menu item
    document.addEventListener('beehiiv:showInstallPrompt', showInstallPrompt);
    return () => {
      document.removeEventListener('beehiiv:showInstallPrompt', showInstallPrompt);
    };
  }, [promptShown, setPromptShown, showPrompt]);

  // only show prompt on mobile devices
  if (!debug && !isAndroid && !isIos) return null;
  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.5)]">
      <div className="absolute bottom-0 w-full p-6 bg-white border rounded-t-xl text-gray-950">
        <div
          className="flex items-center justify-between"
          onClick={() => {
            setShowPrompt(false);
            setPromptShown(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowPrompt(false);
              setPromptShown(true);
            }
          }}
        >
          <h1 className="text-2xl font-bold">{t('pwa.install_prompt.install_prompt')}</h1>
          <div>
            <XMarkIcon className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-4 p-4 mt-6 bg-gray-100 border border-gray-200 rounded">
          <img src={publication.logo.thumb.url} alt={publication.name} className="w-16 h-16 rounded-lg overflow-clip" />
          <div>
            <h2 className="text-lg font-semibold">{publication.name}</h2>
            <p>{publication.configured_domain}</p>
          </div>
        </div>

        {isAndroid && (
          <div className="flex flex-col items-center mx-6 my-8">
            <Button type="button" variant="primary" onClick={onInstall}>
              {t('pwa.install_prompt.install_cta')}
            </Button>
            <p className="mt-4">{t('pwa.install_prompt.install_sub_cta')}</p>
          </div>
        )}
        {isIos && (
          <ol className="mx-6 mt-4 mb-4 space-y-6 list-decimal">
            <li className="relative pl-0">
              {t('pwa.install_prompt.tap_on')}{' '}
              <div className="relative inline-block px-1 py-1 text-blue-400 bg-gray-100 border-gray-200 top-2">
                <IosShareIcon className="w-6 h-6" />
              </div>{' '}
              {t('pwa.install_prompt.in_the_browser')}
            </li>
            <li className="pl-0">
              {t('pwa.install_prompt.scroll_down')}{' '}
              <span className="inline-block px-1 py-1 text-sm font-semibold bg-gray-100 border-gray-200">
                {t('pwa.install_prompt.add_to_home_screen')}
              </span>
            </li>
            <li className="pl-0">
              {t('pwa.install_prompt.look_for')}{' '}
              <img
                src={publication.logo.thumb.url}
                alt={publication.name}
                className="inline-block w-6 h-6 rounded-lg overflow-clip"
              />{' '}
              {t('pwa.install_prompt.icon_on_home_screen')}
            </li>
          </ol>
        )}
      </div>
    </div>
  );
}

function usePromptShown(): [boolean, (value: boolean) => void] {
  let initialPromptShown = false;
  if (typeof document !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      initialPromptShown = localStorage.getItem('pwaPromptShown') === 'true';
    } catch (e) {
      // localStorage access can throw in secure contexts or when disabled
      console.warn('localStorage access denied:', e);
    }
  }
  const [promptShown, setPromptShown] = useState<boolean>(initialPromptShown);

  useEffect(() => {
    // invent listener enables the prompt to be shown again
    // from the install menu item
    document.addEventListener('beehiiv:showInstallPrompt', showInstallPrompt);
    return () => {
      document.removeEventListener('beehiiv:showInstallPrompt', showInstallPrompt);
    };
  }, []);

  const setPromptShownAndStore = (value: boolean) => {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('pwaPromptShown', String(value));
      } catch (e) {
        // localStorage access can throw in secure contexts or when disabled
        console.warn('localStorage access denied:', e);
      }
    }
    setPromptShown(value);
  };

  return [promptShown, setPromptShownAndStore];
}

export default InstallPrompt;
